import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  Loader2,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Timer,
  Users,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import VoiceAnalyticsCard from "@/components/VoiceAnalyticsCard";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const RANGES = [
  { key: 7, label: "7d" },
  { key: 30, label: "30d" },
  { key: 90, label: "90d" },
];

function Stat({ label, value, sublabel, accent, Icon, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="label-eyebrow">{label}</div>
          <div className="mt-2 font-heading text-2xl font-semibold text-clinical-text">
            {value}
          </div>
          {sublabel ? (
            <div className="text-[11px] text-clinical-subtext mt-0.5">{sublabel}</div>
          ) : null}
        </div>
        <span
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${accent}1a`, color: accent }}
        >
          <Icon className="h-5 w-5" />
        </span>
      </div>
    </div>
  );
}

export default function AdminAnalytics() {
  const [days, setDays] = useState(30);
  const [overview, setOverview] = useState(null);
  const [trend, setTrend] = useState([]);
  const [scorecards, setScorecards] = useState([]);
  const [incidents, setIncidents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/admin/analytics/overview", { params: { days } }),
      api.get("/admin/analytics/reports-trend", { params: { weeks: 12 } }),
      api.get("/admin/analytics/caregiver-scorecards", { params: { days } }),
      api.get("/admin/analytics/incidents", { params: { days: Math.max(days, 90) } }),
    ])
      .then(([o, t, s, i]) => {
        setOverview(o.data);
        setTrend(t.data.items || []);
        setScorecards(s.data.items || []);
        setIncidents(i.data);
      })
      .finally(() => setLoading(false));
  }, [days]);

  if (loading || !overview) {
    return (
      <div className="flex items-center justify-center py-20 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading analytics…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-analytics-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">Insights</div>
          <h1 className="mt-1 text-3xl font-heading font-semibold tracking-tight">
            Reports &amp; Analytics
          </h1>
          <p className="mt-1 text-sm text-clinical-subtext">
            Trends, incidents, completion rates, and caregiver performance.
          </p>
        </div>
        <div className="flex items-center gap-1.5 bg-clinical-bg p-1 rounded-lg" data-testid="analytics-range">
          {RANGES.map((r) => (
            <button
              key={r.key}
              type="button"
              onClick={() => setDays(r.key)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                days === r.key
                  ? "bg-white text-clinical-text shadow-sm"
                  : "text-clinical-subtext hover:text-clinical-text"
              }`}
              data-testid={`range-${r.key}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Reports submitted"
          value={overview.reports}
          sublabel={`Across ${overview.active_caregivers} active caregivers`}
          accent="#004AE6"
          Icon={ClipboardList}
          testid="analytics-stat-reports"
        />
        <Stat
          label="Visit completion"
          value={overview.completion_pct == null ? "—" : `${overview.completion_pct}%`}
          sublabel={`${overview.visits_completed} done · ${overview.visits_missed} missed`}
          accent="#10B981"
          Icon={CheckCircle2}
          testid="analytics-stat-completion"
        />
        <Stat
          label="Hours logged"
          value={overview.hours_logged}
          sublabel="From clocked shifts"
          accent="#FF46A3"
          Icon={Timer}
          testid="analytics-stat-hours"
        />
        <Stat
          label="Incidents"
          value={overview.incidents}
          sublabel={`${overview.late_arrivals} late arrivals`}
          accent="#EF4444"
          Icon={AlertTriangle}
          testid="analytics-stat-incidents"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card-surface p-5 lg:col-span-2" data-testid="analytics-trend-chart">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-heading text-lg font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-clinical-primary" />
              Reports &amp; incidents — last 12 weeks
            </h2>
          </div>
          <div className="h-64 min-h-[220px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trend} margin={{ top: 10, right: 10, bottom: 0, left: -10 }}>
                <defs>
                  <linearGradient id="reps" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#004AE6" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#004AE6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="incs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="label" stroke="#4B5563" fontSize={10} />
                <YAxis stroke="#4B5563" fontSize={10} allowDecimals={false} />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Area type="monotone" dataKey="reports" stroke="#004AE6" fill="url(#reps)" strokeWidth={2} />
                <Area type="monotone" dataKey="incidents" stroke="#EF4444" fill="url(#incs)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-5" data-testid="incident-breakdown">
          <h2 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Top clients by incidents
          </h2>
          {incidents?.by_client?.length ? (
            <ul className="space-y-2">
              {incidents.by_client.slice(0, 6).map((c) => (
                <li
                  key={c.client_id}
                  className="flex items-center justify-between text-sm"
                  data-testid={`incident-client-${c.client_id}`}
                >
                  <span className="truncate">{c.client_name || "—"}</span>
                  <span className="pill bg-destructive/10 text-destructive">{c.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="py-6 text-center text-sm text-clinical-subtext">No incidents in this window.</div>
          )}
        </div>
      </div>

      <div className="card-surface p-5" data-testid="caregiver-scorecards">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-lg font-medium flex items-center gap-2">
            <Users className="h-4 w-4 text-clinical-primary" />
            Caregiver scorecards
          </h2>
          <Link
            to="/admin/team/caregivers"
            className="text-xs text-clinical-primary hover:underline inline-flex items-center gap-1"
            data-testid="scorecards-team-link"
          >
            Open Caregivers <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[680px]">
            <thead className="border-b border-clinical-border text-clinical-subtext text-xs uppercase">
              <tr>
                <th className="text-left py-2 px-2">Caregiver</th>
                <th className="text-right py-2 px-2">Reports</th>
                <th className="text-right py-2 px-2">Visits done</th>
                <th className="text-right py-2 px-2">Missed</th>
                <th className="text-right py-2 px-2">Completion</th>
                <th className="text-right py-2 px-2">Late</th>
                <th className="text-right py-2 px-2">Incidents</th>
                <th className="text-right py-2 px-2">Hours</th>
              </tr>
            </thead>
            <tbody>
              {scorecards.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-clinical-subtext text-sm">
                    No caregivers yet.
                  </td>
                </tr>
              ) : (
                scorecards.map((s) => {
                  const lowCompletion = s.completion_pct != null && s.completion_pct < 80;
                  return (
                    <tr
                      key={s.user_id}
                      className="border-b border-clinical-border/60"
                      data-testid={`scorecard-${s.user_id}`}
                    >
                      <td className="py-2 px-2">
                        <div className="font-medium text-clinical-text">{s.name}</div>
                        <div className="text-[11px] text-clinical-subtext">{s.email}</div>
                      </td>
                      <td className="py-2 px-2 text-right">{s.reports}</td>
                      <td className="py-2 px-2 text-right">{s.visits_done}</td>
                      <td className={`py-2 px-2 text-right ${s.visits_missed > 0 ? "text-destructive font-medium" : ""}`}>
                        {s.visits_missed}
                      </td>
                      <td className="py-2 px-2 text-right">
                        {s.completion_pct == null ? (
                          <span className="text-clinical-subtext">—</span>
                        ) : (
                          <span className={lowCompletion ? "text-amber-700 font-medium" : ""}>
                            {s.completion_pct}%
                          </span>
                        )}
                      </td>
                      <td className={`py-2 px-2 text-right ${s.late_arrivals > 0 ? "text-amber-700" : ""}`}>
                        {s.late_arrivals}
                      </td>
                      <td className={`py-2 px-2 text-right ${s.incidents > 0 ? "text-destructive" : ""}`}>
                        {s.incidents}
                      </td>
                      <td className="py-2 px-2 text-right">{s.hours}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card-surface p-5" data-testid="incident-trend">
        <h2 className="font-heading text-lg font-medium mb-3 flex items-center gap-2">
          <Clock className="h-4 w-4 text-clinical-subtext" />
          Incidents per week
        </h2>
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={incidents?.by_week || []} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
              <CartesianGrid stroke="#E5E7EB" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" stroke="#4B5563" fontSize={10} />
              <YAxis stroke="#4B5563" fontSize={10} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12, fontSize: 12 }} />
              <Bar dataKey="count" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <VoiceAnalyticsCard />
    </div>
  );
}
